import type { Partner } from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { Handshake, Plus } from 'lucide-react'
import { usePartners } from '~/entities/partner/model/use-partners'
import { PartnerFormDialog } from '~/features/admin/partner-form-dialog'
import { PartnerLink, partnerKindLabel } from '~/features/admin/partner-link'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card, CardContent } from '~/shared/ui/card'
import { Skeleton } from '~/shared/ui/skeleton'

function PartnerRow({ partner }: { partner: Partner }) {
  const locale = getLocale()
  const count = (value: number) => new Intl.NumberFormat(locale).format(value)
  const { stats } = partner

  return (
    <tr className="border-border border-b last:border-0">
      <td className="py-3 pr-4">
        <div className="flex items-center gap-2">
          <Link
            to="/admin/partners/$partnerId"
            params={{ partnerId: partner.id }}
            className="font-medium hover:underline"
          >
            {partner.name}
          </Link>
          <Badge variant="outline" className="text-xs">
            {partnerKindLabel(partner.kind)}
          </Badge>
        </div>
      </td>
      <td className="py-3 pr-4">
        <PartnerLink partner={partner} />
      </td>
      <td className="py-3 pr-4 font-semibold tabular-nums">{count(stats.businesses)}</td>
      <td className="py-3 pr-4 tabular-nums">{count(stats.activeBusinesses)}</td>
      <td className="py-3 pr-4 tabular-nums">{count(stats.lunchMenus)}</td>
      <td className="py-3 pr-4 tabular-nums">{count(stats.menuScans)}</td>
      <td className="py-3 pr-4 tabular-nums">{count(stats.eventsPublished)}</td>
      <td className="py-3 pr-4 tabular-nums">{count(stats.paidBusinesses)}</td>
      <td className="py-3 pr-4 text-muted-foreground text-sm whitespace-nowrap">
        {new Date(partner.createdAt).toLocaleDateString(locale, { dateStyle: 'medium' })}
      </td>
    </tr>
  )
}

export function PartnersPage() {
  const query = usePartners()
  const partners = query.data ?? []

  return (
    <div className="px-4 sm:px-8 py-8 sm:py-16">
      <div className="space-y-8 mx-auto max-w-6xl">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div className="space-y-1.5">
            <h1 className="flex items-center gap-2.5 font-semibold text-foreground text-3xl tracking-tight">
              <Handshake className="w-7 h-7" aria-hidden />
              {m.partnersTitle()}
            </h1>
            <p className="max-w-3xl text-muted-foreground text-sm">{m.partnersHelp()}</p>
          </div>
          <PartnerFormDialog
            trigger={
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                {m.partnersCreate()}
              </Button>
            }
          />
        </header>

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            {query.isLoading ? (
              <div className="space-y-3 p-6">
                {['a', 'b', 'c'].map((placeholder) => (
                  <Skeleton key={placeholder} className="w-full h-10" />
                ))}
              </div>
            ) : partners.length === 0 ? (
              <div className="flex flex-col items-center gap-2 p-12 text-center">
                <Handshake className="w-8 h-8 text-muted-foreground" aria-hidden />
                <p className="text-muted-foreground text-sm">{m.partnersEmpty()}</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-border border-b text-muted-foreground text-xs text-left uppercase tracking-wide">
                    <th className="py-3 pr-4 pl-4 font-medium">{m.partnersColumnName()}</th>
                    <th className="py-3 pr-4 font-medium">{m.partnersColumnLink()}</th>
                    <th className="py-3 pr-4 font-medium">{m.partnersColumnBusinesses()}</th>
                    <th className="py-3 pr-4 font-medium" title={m.partnersStatActiveHint()}>
                      {m.partnersColumnActive()}
                    </th>
                    <th className="py-3 pr-4 font-medium">{m.partnersColumnLunchMenus()}</th>
                    <th className="py-3 pr-4 font-medium">{m.partnersColumnMenuScans()}</th>
                    <th className="py-3 pr-4 font-medium">{m.partnersColumnEvents()}</th>
                    <th className="py-3 pr-4 font-medium">{m.partnersColumnPaid()}</th>
                    <th className="py-3 pr-4 font-medium">{m.partnersColumnCreated()}</th>
                  </tr>
                </thead>
                <tbody className="[&_td:first-child]:pl-4">
                  {partners.map((partner) => (
                    <PartnerRow key={partner.id} partner={partner} />
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
